import { faCheckCircle, faTimes } from "@fortawesome/free-solid-svg-icons";
import classnames from "classnames";
import Button from "components/components/Button/Button";
import Icon from "components/components/Icon/Icon";
import { IconSize } from "components/components/Icon/IconWrapper";
import LoadingSpinner from "components/components/LoadingSpinner/LoadingSpinner";
import { ToastDetails, ToastState } from "models/ToastDetails";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import colours from "styles/colours";

import {
  ExitButtonContainer,
  LoadingSpinnerContainer,
  IconContainer,
  StyledToast,
  ToastContent,
} from "./style";

interface AppToastProps {
  details: ToastDetails;
  onDismiss?: () => void;
  dataTestId?: string;
}

const DISMISS_TIMEOUT_MILLISECONDS = 3000;

export const AppToast: React.FC<AppToastProps> = ({
  details,
  onDismiss,
  dataTestId = "toast-container",
}) => {
  const [dismissed, setDismissed] = useState(false);
  const [dismissTimeout, setDismissTimeout] = useState<NodeJS.Timeout>();

  const dismiss = useCallback((): void => {
    toast.dismiss(details.id);
    setDismissed(true);
    onDismiss?.();
  }, [details.id, onDismiss]);

  useEffect(() => {
    if (
      details.state === ToastState.Complete ||
      details.state === ToastState.Error
    ) {
      setDismissTimeout(setTimeout(dismiss, DISMISS_TIMEOUT_MILLISECONDS));
    } else if (dismissTimeout) {
      clearTimeout(dismissTimeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updating on dismissTimeout is unnecessary and causes a loop
  }, [details, dismiss]);

  const toastIcon = (state: ToastState): JSX.Element => {
    switch (state) {
      case ToastState.Complete:
        return (
          <Icon
            iconName={faCheckCircle}
            colour={colours.elexonBlue}
            ariaLabel="Complete"
            size={IconSize.medium}
          />
        );
      case ToastState.Error:
        return (
          <Icon
            iconName={faTimes}
            colour={colours.elexonBlue}
            ariaLabel="Error"
            size={IconSize.medium}
          />
        );
      case ToastState.InProgress:
        return (
          <LoadingSpinnerContainer>
            <LoadingSpinner isLoading />
          </LoadingSpinnerContainer>
        );
      default:
        return <></>;
    }
  };

  return (
    <StyledToast
      className={classnames({ dismissed })}
      data-test-id={dataTestId}
    >
      <IconContainer>{toastIcon(details.state)}</IconContainer>
      <ToastContent>
        <h4 className="title" data-test-id="toast-title">
          {details.title}
        </h4>
        {details.message && (
          <p data-test-id="toast-message">{details.message}</p>
        )}
      </ToastContent>
      <ExitButtonContainer>
        <Button
          className="plain-inline"
          ariaLabel="Dismiss"
          buttonText=""
          iconName={faTimes}
          onClick={dismiss}
        />
      </ExitButtonContainer>
    </StyledToast>
  );
};

export const createOrUpdateToast = (
  toastDetails: ToastDetails,
  duration: number,
  onDismiss?: () => void,
  dataTestId?: string
): string =>
  toast.custom(
    <AppToast
      details={toastDetails}
      onDismiss={onDismiss}
      dataTestId={dataTestId}
    />,
    {
      id: toastDetails.id,
      duration,
    }
  );
